import { mediaSchema } from "@/components/Forms/schema/mediaSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    handleJsonApiError,
} from "jsonapi-zod-query";

const resourceSelector = createResourceCollectionSelector({
    type: "media",
    attributesSchema: mediaSchema,
});

const mediaCollectionSelector = createDataSelector(resourceSelector);

export type MediaCollection = ReturnType<typeof mediaCollectionSelector>;
export type Media = MediaCollection[number];

type Props = {
    companyUUID: string;
};

export const useFetchMediasForCompany = ({
    companyUUID,
}: Props): UseQueryResult<MediaCollection> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["company-media", companyUUID],
        queryFn: async () => {
            const fetchURL = apiUrl("/media/for-company");
            fetchURL.searchParams.set("companyUUID", companyUUID);

            const response = await fetch(fetchURL.href, {
                method: "POST",
                headers: {
                    accept: "application/vnd.api+json",
                },
                body: JSON.stringify({
                    companyUUID: companyUUID,
                }),
            });

            await handleJsonApiError(response);
            return await response.json();
        },
        select: mediaCollectionSelector,
    });
};
